// src/pages/CookiePolicyPage.js
//
// Cookie Policy. Layout chrome lives in <LegalPageShell>.

import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import LegalPageShell from '../components/LegalPageShell';

const CookiePolicyPage = () => {
  useEffect(() => {
    document.title = 'Cookie Policy | Nexus';
  }, []);

  return (
    <LegalPageShell title="Cookie Policy" updated="February 7, 2026">
      <p className="intro-text">
        This Cookie Policy explains how Nexus ("we," "us," or "our") uses cookies, local storage, and similar technologies when you use our platform. We are committed to transparency about the technologies we use.
      </p>

      <h2>1. Our Approach</h2>
      <p>
        Nexus takes a privacy-first approach. We do <strong>not</strong> use traditional browser cookies for tracking, analytics, or advertising purposes. We do not use any third-party analytics tools such as Google Analytics, and we do not deploy tracking pixels or behavioral profiling tools.
      </p>
      <p>
        All data we store on your device is <strong>strictly functional</strong> — meaning it is necessary for the Service to operate correctly.
      </p>

      <h2>2. What We Store on Your Device</h2>
      <p>Instead of cookies, we use your browser's <strong>local storage</strong> and <strong>session storage</strong> to save essential data. These technologies store data locally on your device and are not sent to third-party servers.</p>

      <h3>Authentication (Local Storage)</h3>
      <ul>
        <li><strong>Session tokens:</strong> Managed by Supabase (our authentication provider) to keep you securely logged in across page refreshes. These are cleared when you log out.</li>
      </ul>

      <h3>User Preferences (Local Storage)</h3>
      <ul>
        <li><strong>Theme:</strong> Your selected color theme (light/dark)</li>
        <li><strong>Accent color:</strong> Your chosen accent color preference</li>
        <li><strong>Background theme:</strong> Your selected background style</li>
      </ul>

      <h3>Application State (Local Storage)</h3>
      <ul>
        <li><strong>Onboarding status:</strong> Whether you have completed the welcome tour, so it is not shown again</li>
        <li><strong>Dismissed banners:</strong> Which informational banners you have dismissed, so they do not reappear</li>
        <li><strong>Recent searches:</strong> Your recent search terms within the app for quick access</li>
        <li><strong>App version:</strong> The last version you viewed, to show update notes for new releases</li>
      </ul>

      <h3>Temporary State (Session Storage)</h3>
      <ul>
        <li><strong>Scanning context:</strong> Temporary state for email scanning operations, cleared when you close the tab</li>
      </ul>

      <h2>3. Third-Party Services</h2>
      <p>Some third-party services we integrate with may use their own cookies or similar technologies:</p>

      <h3>Stripe (Payment Processing)</h3>
      <p>
        When you subscribe to a paid plan, Stripe may set cookies on your browser during the checkout process. These cookies are essential for secure payment processing and fraud prevention. We do not control Stripe's cookies.
        Learn more: <a href="https://stripe.com/privacy" target="_blank" rel="noopener noreferrer">Stripe Privacy Policy</a>.
      </p>

      <h3>Sentry (Error Monitoring)</h3>
      <p>
        We use Sentry in production to detect and fix bugs. Sentry collects error reports along with basic context (such as browser type and your user ID) to help us diagnose issues. It does <strong>not</strong> track your browsing behavior or build user profiles.
        Learn more: <a href="https://sentry.io/privacy/" target="_blank" rel="noopener noreferrer">Sentry Privacy Policy</a>.
      </p>

      <h3>Google Fonts (CDN)</h3>
      <p>
        We load fonts from Google's content delivery network. When your browser requests these fonts, Google may log your IP address in accordance with their standard practices.
        Learn more: <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">Google Privacy Policy</a>.
      </p>

      <h2>4. What We Do NOT Use</h2>
      <p>To be clear, Nexus does <strong>not</strong> use:</p>
      <ul>
        <li>Analytics cookies (no Google Analytics, Mixpanel, etc.)</li>
        <li>Advertising cookies or retargeting pixels</li>
        <li>Social media tracking cookies</li>
        <li>Cross-site tracking of any kind</li>
        <li>Behavioral profiling or fingerprinting</li>
      </ul>

      <h2>5. Managing Stored Data</h2>
      <p>You have full control over data stored on your device:</p>
      <ul>
        <li><strong>Clear all site data:</strong> Use your browser settings to clear local storage and session storage for our domain. This will log you out and reset your preferences.</li>
        <li><strong>Browser developer tools:</strong> You can inspect and selectively delete stored items via your browser's developer tools (Application tab).</li>
        <li><strong>Log out:</strong> Logging out of Nexus clears your authentication tokens automatically.</li>
      </ul>
      <p>
        Note: Clearing local storage will not affect your account data, which is stored securely on our servers. Your inventory, sales, and other business data will remain intact.
      </p>

      <h2>6. Do Not Track</h2>
      <p>
        Since we do not use tracking cookies or analytics tools, the "Do Not Track" browser setting does not change how Nexus operates — we do not track you regardless of this setting.
      </p>

      <h2>7. Updates to This Policy</h2>
      <p>
        We may update this Cookie Policy from time to time to reflect changes in our practices or for legal, operational, or regulatory reasons. We will update the "Last updated" date at the top of this page.
      </p>

      <h2>8. Contact Us</h2>
      <p>
        If you have questions about this Cookie Policy, please contact us through our <Link to="/support">Support page</Link>.
      </p>
      <p>
        For more information about how we handle your data overall, see our <Link to="/privacy">Privacy Policy</Link>.
      </p>
    </LegalPageShell>
  );
};

export default CookiePolicyPage;
